import { Stack, Switch } from '@mui/material';
import { memo, useCallback, useMemo, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { WorkspaceMode } from '@modules/common/types/general';
import { LogicalSpace } from '@thive/fddm-types';
import { useAppStore } from '@/modules/app/store/useAppStore';


import { isDoNotStop, isRestrictedAccess, isSpeedLimit, isVehicleMode } from '../guards';

import { MaxSpeedRule } from './SpeedLimitRule';
import { VehicleModeRule } from './VehicleModeRule';
import { useLogicalSpaceTrafficRuleActions } from '../hooks/useTrafficRulesActions';

interface Props {
  logicalSpace: LogicalSpace;
}

function TrafficRulePropertiesComponent({ logicalSpace }: Props) {
  const mode = useAppStore((state) => state.mode);
  const { t } = useTranslation('interface');

  const { setDoNotStopEnabled, setRestrictedAccessEnabled, setMaxSpeed, toggleVehicleMode } =
    useLogicalSpaceTrafficRuleActions(logicalSpace);

  const doNotStopEnabled = useMemo(
    () => logicalSpace.trafficRules.some((rule) => isDoNotStop(rule)),
    [logicalSpace.trafficRules],
  );

  const restrictedAccessEnabled = useMemo(
    () => logicalSpace.trafficRules.some((rule) => isRestrictedAccess(rule)),
    [logicalSpace.trafficRules],
  );

  const speedLimitRule = useMemo(
    () => logicalSpace.trafficRules.find(isSpeedLimit),
    [logicalSpace.trafficRules],
  );

  const vehicleModeRules = useMemo(
    () => logicalSpace.trafficRules.filter(isVehicleMode),
    [logicalSpace.trafficRules],
  );

  const handleDoNotStopChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDoNotStopEnabled(event.target.checked);
    },
    [setDoNotStopEnabled],
  );

  const handleRestrictedAccessChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setRestrictedAccessEnabled(event.target.checked);
    },
    [setRestrictedAccessEnabled],
  );

  return (
    <Accordion
      title={t('traffic_rules.header', 'Traffic rules')}
      titleSx={{ fontWeight: 400 }}
      defaultExpanded
      unmountOnExit
      sx={{ px: 2 }}
    >
      <Stack direction='column' spacing={1}>
        <PropertiesLabel i18nkey='interface:traffic_rules.options.do_not_stop'>
          <Stack direction='row' justifyContent='flex-end'>
            <Switch
              checked={doNotStopEnabled}
              onChange={handleDoNotStopChange}
              disabled={mode !== WorkspaceMode.EDITABLE}
              inputProps={{
                'aria-label': t('traffic_rules.options.do_not_stop', 'Do not stop'),
              }}
            />
          </Stack>
        </PropertiesLabel>

        <PropertiesLabel
          i18nkey='interface:traffic_rules.options.restricted_access'
          labelRatio={70}
        >
          <Stack direction='row' justifyContent='flex-end'>
            <Switch
              checked={restrictedAccessEnabled}
              onChange={handleRestrictedAccessChange}
              disabled={mode !== WorkspaceMode.EDITABLE}
              inputProps={{
                'aria-label': t('traffic_rules.options.restricted_access', 'Restricted access'),
              }}
            />
          </Stack>
        </PropertiesLabel>

        <MaxSpeedRule
          rule={speedLimitRule}
          onChange={setMaxSpeed}
          disabled={mode !== WorkspaceMode.EDITABLE}
        />

        <VehicleModeRule
          rules={vehicleModeRules}
          onToggle={toggleVehicleMode}
          disabled={mode !== WorkspaceMode.EDITABLE}
        />
      </Stack>
    </Accordion>
  );
}

export const TrafficRuleProperties = memo(TrafficRulePropertiesComponent);
export default TrafficRuleProperties;
